import React from 'react';
import {useTranslation} from "react-i18next";
import AssetNameplate from '../AssetNameplate';
import { SideNavigation } from '@dreebit-registry/vsm-microservice-components';
import { Box } from '@mui/material';
import {InstalledBaseNodeTypeKey} from "../../codegen/graphql";
import {isIotAttachEntryVisible} from "../../utils/iotAttachGate";


interface Props {
    assetLocationId: string;
    installedBaseNodeId: string;
    installedBaseNodeTypeKey?: InstalledBaseNodeTypeKey;
    isInstalled: boolean;
    hasIotConnection: boolean;
    isPendingIotConnection: boolean;
    isContractValid: boolean;
    isContractExpired: boolean;
    isIoTAdmin: boolean;
    hasServiceReminders: boolean;
    isSemiconductor: boolean;
    /** IoT admin and the device has an SLA status (IBM-1045) — offers the SLA report. */
    hasSlaStatus: boolean;
    /** Feature flag on and the newest removal has a declaration or may start one. */
    showContaminationDeclarations: boolean;
}

const AssetDetailNavigation = ({ assetLocationId, isInstalled, hasIotConnection, isPendingIotConnection, isContractValid, isContractExpired, isIoTAdmin, hasServiceReminders, isSemiconductor, hasSlaStatus, showContaminationDeclarations }: Props) => {
    const {t} = useTranslation();

    // IoT admins may view existing data for an expired or missing contract; connecting
    // still needs a valid one. Keep in sync with canViewIoT (src/utils/isContractValid.ts).
    const canMonitorIoT = isContractValid || isIoTAdmin;
    // Non-admins keep seeing the Condition Monitoring entry after expiry, but locked.
    const isLockedIoT = isContractExpired && !isIoTAdmin;

    // Service reminders are the manual alternative to IoT monitoring: always shown for
    // semiconductor and non-IoT assets, and kept visible for IoT assets that already have
    // reminders. Mirrors the dashboard's reminders widget visibility.
    const showServiceReminders = isInstalled && (isSemiconductor || !hasIotConnection || hasServiceReminders);

    // Same frontend rule as the connect route guard (src/utils/iotAttachGate.ts); the
    // network scan gate is not checked here — the wizard explains a blocked scan.
    const iotConnectionItem = isIotAttachEntryVisible({
        isIoTAdmin, isInstalled, hasIotConnection, isPendingIotConnection,
        contractValid: isContractValid, isSemiconductor,
    })
        ? { label: t('IoT Connection'), to: `/assets/${assetLocationId}/connect` }
        : null;

    const navItems = [
        { label: t('Dashboard'), to: `/assets/${assetLocationId}/dashboard` },
        ...((hasIotConnection || isPendingIotConnection) && (canMonitorIoT || isLockedIoT) ? [{
            label: t('Condition Monitoring'),
            ...(isLockedIoT ? { locked: true, lockedTooltip: t('The IoT contract for this asset has expired.') } : {}),
            subItems: [
                { label: t('Telemetry'), to: `/assets/${assetLocationId}/conditionMonitoring/telemetry` },
                { label: t('Maintenance'), to: `/assets/${assetLocationId}/conditionMonitoring/maintenance` },
                ...(hasSlaStatus ? [{ label: t('SLA Report'), to: `/assets/${assetLocationId}/conditionMonitoring/slaReport` }] : []),
                { label: t('Alerts'), to: `/assets/${assetLocationId}/conditionMonitoring/alerts` },
            ]
        }] : []),
        { label: t('Specifications'), to: `/assets/${assetLocationId}/specifications` },
        ...(showServiceReminders ? [{ label: t('Service Reminders'), to: `/assets/${assetLocationId}/serviceReminders` }] : []),
        ...(iotConnectionItem ? [iotConnectionItem] : []),
        { label: t('Documents'), to: `/assets/${assetLocationId}/files` },
        { label: t('Event History'), to: `/assets/${assetLocationId}/events` },
        ...(showContaminationDeclarations
            ? [{ label: t('Contamination Declarations'), to: `/assets/${assetLocationId}/contaminationDeclarations` }]
            : []),
    ];

    return (
        <div className="flex-col">
            <AssetNameplate assetLocationId={assetLocationId}/>

            <Box sx={{pt: "8px"}}>
                <SideNavigation key={`nav-${hasIotConnection}-${isPendingIotConnection}-${canMonitorIoT}-${isLockedIoT}-${showServiceReminders}-${hasSlaStatus}-${showContaminationDeclarations}`} navItems={navItems}/>
            </Box>
        </div>
    );
};

export default AssetDetailNavigation;
